import { Component, inject, Input, signal } from '@angular/core';
import { RentalContract } from '@modules/universal-billing/contract/contract.interface';
import { BasicForm } from '@shared/components/classes/basic.form';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContractService } from '@modules/universal-billing/contract/contract.service';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { CalendarModule } from '@primeng/calendar';
import { CheckboxModule } from '@primeng/checkbox';
import { InputLabelComponent } from '@shared/components/input-label/input-label.component';
import { InputNumberModule } from '@primeng/inputnumber';
import { InputValidatorDirective } from '@common/directive/input-validator.directive';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { TranslatePipe } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { Observable } from 'rxjs';
import { removeEmptyProperties } from '@shared/utils/object.utils';
import { formatDate } from '@shared/utils/format-date';
import { ResponseSuccess } from '@shared/components/classes/page-result';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { RentType } from '@modules/universal-billing/residents/resident.enum';
import {DatepickerComponent} from '@shared/components/datepicker/datepicker.component';

@Component({
  selector: 'app-contract-details-form',
  standalone: true,
  imports: [
    AutocompleteComponent,
    CalendarModule,
    CheckboxModule,
    InputLabelComponent,
    InputNumberModule,
    InputValidatorDirective,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgSelectComponent,
    ReactiveFormsModule,
    TranslatePipe,
    NgIf,
    NgbAlert,
    DatepickerComponent,
  ],
  templateUrl: './contract-details-form.component.html',
  styleUrl: './contract-details-form.component.scss'
})
export class ContractDetailsFormComponent extends BasicForm<any> {
  @Input() contract: RentalContract;
  @Input() afterSuccess: Function;

  _defaultService: ContractService = inject(ContractService);

  isEdit = true;
  hasAvailable = signal(true);

  form: FormGroup = new FormGroup({
    rentType: new FormControl(null, [Validators.required]),
    startDate: new FormControl(null, Validators.required),
    endDate: new FormControl(),
    permanent: new FormControl(true),
    residentsQuantity: new FormControl(null, Validators.required),
    notes: new FormControl(''),
    roomsQuantity: new FormControl(null, Validators.required),
  });

  dateFields = ['startDate', 'endDate'];
  minEndDate: Date;
  ngOnInit() {

    this.form.get('startDate')?.valueChanges.subscribe(start => {
      const endControl = this.form.get('endDate');
      const end = endControl?.value;

      if (end && new Date(end) <= new Date(start)) {
        endControl?.setValue(null, { emitEvent: false });
      }
      this.minEndDate = new Date(start);
      this.minEndDate.setDate(this.minEndDate.getDate() + 1);
    });
    this.patchResponseData({...this.contract });

    this.form.get('permanent').valueChanges.subscribe(value => {
      this.changeForm(value);
    })
    this.changeForm(this.form.value.permanent);
  }

  changeForm(value: boolean) {
    const endForm = this.form.get('endDate');
    if (value) {
      endForm.setValidators([]);
      endForm.disable();
    }else {
      endForm.setValidators([Validators.required]);
      endForm.enable();
    }
  }
  onSubmit() {
    const rentalDetails = this.form.value;
    this.markAllControls(this.form);
    if(this.form.valid && !this.isPending()) {
      this.isPending.set(true);
      this._defaultService.checkAvailability(removeEmptyProperties({
        rentType: rentalDetails.rentType,
        startDate: formatDate(rentalDetails.startDate),
        endDate: rentalDetails.permanent ? null : formatDate(rentalDetails.endDate),
        apartmentId: rentalDetails.rentType == RentType.APARTMENT? this.contract.apartmentId : null,
        buildingId: rentalDetails.rentType == RentType.BUILDING ? this.contract.buildingId : null,
        rentalContractId: this.contract.id,
        roomsQuantity: rentalDetails.roomsQuantity,
      })).subscribe((res: ResponseSuccess<[]>) => {
        if (res.success) {
          this.submit();
        }else {
          this.hasAvailable.set(false);
          this.isPending.set(false);
        }
      }, () => {
        this.isPending.set(false);
      });
    }

  }
  submit() {
    this.beforeSubmit();
    const data = this.convertToRequestData(this.form.value);
    this.submitAction(data)
      .subscribe({
        next: (res) => this.afterSuccessSubmit(res),
        error: (res: HttpErrorResponse) => this.afterErrorSubmit(res),
      });
  }
  patchResponseData(data: any) {
    super.patchResponseData(data);
  }

  convertToRequestData(data: any): any {
    return super.convertToRequestData({ ...this.contract, ...data});
  }

  submitAction(data: any): Observable<any> {
    return this._defaultService.patch(data, this.contract.id);
  }

  afterSuccessSubmit(res: any) {
    this.afterSuccess(res.data);
    super.afterSuccessSubmit(res);
  }

}
